import { DeeplyReadonly, Filter, Query } from '@cubejs-client/core';
import { useCubeQuery } from '@cubejs-client/react';
import { pipe, sortWith } from 'ramda';

import { CubeQueryData } from '~/common/types';
import {
  SourcingProjectRequestField,
  SourcingProjectRequestSegment,
} from '~/common/types/cubes/SourcingProjectRequest';
import { useModuleContext } from '~/components/ModuleContextProvider';

interface ItemWithPosition {
  [key: string]: string | number;
  position: string;
}

const dropPrefix = <I extends Record<string, any>, R extends Record<string, any>>(items: I[]): R[] =>
  items.map(
    (item) =>
      Object.entries<I>(item).reduce<R>(
        (acc, entry) => ({ ...acc, [`${entry[0].split('.')[1]}`]: entry[1] }),
        {} as R,
      ) as R,
  );

const dimensions = [
  SourcingProjectRequestField.SOURCING_PROJECT_ID,
  SourcingProjectRequestField.SOURCING_PROJECT_LOT_ID,
  SourcingProjectRequestField.LATEST_ROUND_NUMBER,
  SourcingProjectRequestField.LOT_ITEM_ID,
  SourcingProjectRequestField.LOT_ITEM_TITLE,
  SourcingProjectRequestField.LOT_ITEM_DESCRIPTION,
  SourcingProjectRequestField.LOT_ITEM_UNIT,
  SourcingProjectRequestField.LOT_TITLE,
  SourcingProjectRequestField.POSITION,
  SourcingProjectRequestField.PRICE_PER_UNIT,
  SourcingProjectRequestField.CURRENCY,
  SourcingProjectRequestField.ROUND_NUMBER,
  SourcingProjectRequestField.QUANTITY,
];
const measures = [SourcingProjectRequestField.SUPPLIERS, SourcingProjectRequestField.STATUS];

const numericSort = pipe(
  sortWith([
    (a: ItemWithPosition, b: ItemWithPosition) => a.position.localeCompare(b.position, undefined, { numeric: true }),
  ]),
);

export const useSourcingProjectRequestList = () => {
  const { entityId } = useModuleContext();
  const filters: Filter[] = [
    {
      member: SourcingProjectRequestField.SOURCING_PROJECT_ID,
      operator: 'equals',
      values: [entityId],
    },
  ];
  const baseQuery: DeeplyReadonly<Query> = {
    dimensions,
    measures,
    filters,
  };
  const awardedItemsQuery = useCubeQuery<CubeQueryData>({
    ...baseQuery,
    segments: [SourcingProjectRequestSegment.AWARDED],
  });
  const latestRoundItemsQuery = useCubeQuery<CubeQueryData>({
    ...baseQuery,
    segments: [SourcingProjectRequestSegment.LATEST_ROUND],
  });
  const latestRoundItems: CubeQueryData[] | undefined = latestRoundItemsQuery.resultSet?.rawData() ?? [];
  const awardedItems: CubeQueryData[] | undefined = awardedItemsQuery.resultSet?.rawData() ?? [];
  const renamedItems: ItemWithPosition[] = dropPrefix<CubeQueryData, ItemWithPosition>([
    ...latestRoundItems,
    ...awardedItems,
  ]);
  const sortedItems = numericSort(renamedItems);

  return {
    loading: awardedItemsQuery.isLoading || latestRoundItemsQuery.isLoading,
    items: sortedItems,
  };
};
